import { Object as FabricObject, TPointerEventInfo, TPointerEvent } from "fabric"
import {isMobile} from "@/utils/common";

const notALeftClick = (e: MouseEvent) => {
  return e.button && e.button !== 1;
}

export const TextMixin: any = {

  _mouseDownHandler({ e }: TPointerEventInfo) {
    const now = Date.now();
    const isDoubleTap = this._lastClickTime && (now - this._lastClickTime < 350);
    this._lastClickTime = now;

    if (isDoubleTap && isMobile() && this.editable) {
       this.enterEditing(e);
       return;
    }
    
    if (
      !this.canvas ||
      !this.editable ||
      notALeftClick(e as MouseEvent) ||
      this.__corner
    ) {
      return;
    }

    // If we are already editing, we want to prioritize cursor placement over dragging
    // unless the user is explicitly holding a modifier key (which we can check later if needed)
    if (this.isEditing) {
      this.setCursorByClick(e);
      this.selectionStart = this.selectionEnd = this.getSelectionStartFromPointer(e);
      this.__selectionStartOnMouseDown = this.selectionStart;
      this.abortCursorAnimation();
      this.renderCursorOrSelection();
      // We process the click for cursor placement, so we don't want to drag
      return;
    }

    if (this.draggableTextDelegate.start(e)) {
      return;
    }
  },

  mouseUpHandler({ e, transform, button }: TPointerEventInfo) {
    const didDrag = this.draggableTextDelegate.end(e);
    if (this.canvas) {
      this.canvas.textEditingManager.unregister(this);
      const activeObject = this.canvas._activeObject;
      if (activeObject && activeObject !== this) {
        return;
      }
    }
    
    if (
      !this.editable ||
      (this.group && !this.group.interactive) ||
      (transform && transform.actionPerformed) ||
      notALeftClick(e as MouseEvent) ||
      didDrag
    ) {
      return;
    }
    
    // Mobile: Enter editing mode when text is already selected (single tap on selected text)
    if (isMobile() && this.selected && !this.isEditing && !this.__corner) {
      // Small delay to distinguish from selection tap
      setTimeout(() => {
        if (this.selected && !this.isEditing && this.canvas?._activeObject === this) {
          this.enterEditing(e);
          if (this.selectionStart === this.selectionEnd) {
            this.initDelayedCursor(true);
          }
        }
      }, 150);
      return;
    }
    
    if (this.__lastSelected && !this.__corner) {
      this.selected = false;
      this.__lastSelected = false;
      this.isEditing = false;
      this._restoreEditingProps();
      this.enterEditing(e);
      if (this.selectionStart === this.selectionEnd) {
        this.initDelayedCursor(true);
      } else {
        this.renderCursorOrSelection();
      }
    } else {
      this.selected = true;
    }
  },

  enterEditing(e: TPointerEvent) {
    if (this.isEditing || !this.editable) {
      return;
    }
    if (this.canvas) {
      this.canvas.calcOffset();
      this.canvas.textEditingManager.exitTextEditing();
    }

    if (e) {
      this.setCursorByClick(e);
    }

    this.isEditing = true;
    if (!this.hiddenTextarea) {
      this.initHiddenTextarea();
    }
    this.hiddenTextarea!.focus();
    this.hiddenTextarea!.value = this.text;
    this._updateTextarea();
    this._saveEditingProps();
    this._setEditingProps();
    this._textBeforeEdit = this.text;

    if (this.canvas) {
      this.canvas.fire('text:editing:entered', { target: this, e });
      this.canvas.requestRenderAll();
    }
    this.initDelayedCursor(true);
  },

  onDblClick(options: TPointerEventInfo) {
    if (!this.editable) return;
    this.enterEditing(options.e);
  },

  exitEditing() {
    const isTextChanged = (this._textBeforeEdit !== this.text);
    this.selectionEnd = this.selectionStart;
    this.isEditing = false;
    this._exitEditing()
    this.abortCursorAnimation();
    this._restoreEditingProps();
    if (this._forceClearCache) {
      this.initDimensions();
      this.setCoords();
    }
    this.fire('editing:exited');
    isTextChanged && this.fire('modified')
    if (this.canvas) {
      this.canvas.off('mouse:move', this.mouseMoveHandler);
      this.canvas.fire('text:editing:exited', { target: this });
      this.canvas.fire('object:modified', { target: this });
    }
    return this;
  },

  mouseMoveHandler(options: any) {
    if (!this.__isMousedown || !this.isEditing) {
      return;
    }
    
    // Don't update text selection if object is being transformed/dragged
    if (this.__corner || this.canvas?._currentTransform) {
      return;
    }
    
    if(this.group){
      options.e._group = this.group;
    }
    
    // Check if pointer is within text bounds
    const pointer = this.canvas!.getPointer(options.e);
    const isInsideText = this.containsPoint(pointer);
    
    // Get selection start from pointer first to check if it's valid
    let newSelectionStart = this.getSelectionStartFromPointer(options.e);
    const textLength = this.text.length;
    
    // If mouse is outside text bounds, don't update selection at all
    if (!isInsideText) {
      return;
    }
    
    // Validate and clamp newSelectionStart to text bounds
    if (newSelectionStart < 0) {
      newSelectionStart = 0;
    } else if (newSelectionStart > textLength) {
      newSelectionStart = textLength;
    }
    
    const currentStart = this.selectionStart;
    const currentEnd = this.selectionEnd;
    
    // Prevent unnecessary updates
    if (
      (newSelectionStart !== this.__selectionStartOnMouseDown || currentStart === currentEnd)
      &&
      (currentStart === newSelectionStart || currentEnd === newSelectionStart)
    ) {
      return;
    }
    
    // Update selection
    if (newSelectionStart > this.__selectionStartOnMouseDown) {
      this.selectionStart = this.__selectionStartOnMouseDown;
      this.selectionEnd = newSelectionStart;
    }
    else {
      this.selectionStart = newSelectionStart;
      this.selectionEnd = this.__selectionStartOnMouseDown;
    }
    
    // Only render if selection actually changed
    if (this.selectionStart !== currentStart || this.selectionEnd !== currentEnd) {
      this.restartCursorIfNeeded();
      this._fireSelectionChanged();
      this._updateTextarea();
      this.renderCursorOrSelection();
    }
    if(this.group){
      delete options.e._group;
    }
  }
}